import { useMemo, useEffect } from "react";
import { Button, Box, AccordionSummary, AccordionDetails, Accordion, Tooltip } from "@mui/material";
import { useSelector } from "react-redux";
import ContextMenu from "../../ContextMenu/ContextMenu";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import {
  ICrewEvent,
  ICrewMember,
  ISelectedCrewDays,
  isIDisplayPairing,
  isINonFlyingAssignment,
  defaultTimezone,
} from "src/redux/reducers/CrewScheduleReducer";
import { PairingContext } from "./PairingContext";
import { NonFlyingAssignmentContext } from "./NonFlyingAssignmentContext";
import { SelectedCrewDayContext } from "./SelectedCrewDayContext";

import { closeContextMenu } from "src/redux/actions/ContextMenu.Actions";
import { runRegsValidation } from "src/redux/actions/CrewSchedule/violations";
import { useDispatch } from "react-redux";
import { IModalInfo } from "src/pages/CrewSchedule/CrewScheduleWindow";
import {
  ICrewsNonFlyingAssignment,
  IDisplayBidNFA,
  IDisplayBidPairing,
  IDisplayPairing,
} from "@vmosolutions/oc-interfaces";
import EngineeringIcon from "@mui/icons-material/Engineering";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import moment from "moment-timezone";
import { ShortCodeChip } from "src/components/ShortCodes/ShortCodeChip";
interface ICrewScheduleContextMenuProps {
  id: string;
  path: string[];
  setActiveEventModal?: (modalInfo: IModalInfo) => void;
  containerRef?: any;
}

const CrewScheduleContextMenu = (props: ICrewScheduleContextMenuProps): JSX.Element => {
  const { id, setActiveEventModal, containerRef } = props;
  const dispatch = useDispatch();
  const selectedCrewEvents: ICrewEvent[] = useSelector((state: IRootState) => state.crewSchedule.selectedCrewEvents);
  const selectedCrewDays: ISelectedCrewDays | undefined = useSelector(
    (state: IRootState) => state.crewSchedule.selectedCrewDays
  );
  const allMenus = useSelector((state: IRootState) => state.contextMenu.menus);
  const crew = useSelector((state: IRootState) => state.crewSchedule.crewMembers);
  const windowProperties = useSelector((state: IRootState) => state.crewSchedule.windowProperties[id]);
  const displayTimezone = windowProperties?.displayTimezone ?? defaultTimezone;
  const selectedCrewMember = useMemo(() => {
    return crew.find((value: ICrewMember) => value.isSelected);
  }, [crew]);

  const headerCrewMember = useMemo(() => {
    if (selectedCrewDays?.crewId) return crew.find((c) => c._id === selectedCrewDays.crewId);
    if (selectedCrewEvents?.length) {
      const evCrewId = selectedCrewEvents[0].crewId;
      return crew.find((c) => c._id === evCrewId);
    }
    return selectedCrewMember;
  }, [crew, selectedCrewDays?.crewId, selectedCrewEvents, selectedCrewMember]);

  const dateLabel = useMemo(() => {
    const times = selectedCrewDays?.selectedTimes ?? [];
    if (times.length === 0) return "";
    if (times.length > 1) return "Multiple Days";
    return moment(times[0]).tz(displayTimezone.timezoneId).format("MMM-DD-YYYY");
  }, [selectedCrewDays?.selectedTimes, displayTimezone.timezoneId]);

  const isSelectedCrewMember = useMemo(() => {
    return selectedCrewMember?._id;
  }, [selectedCrewMember]);

  const selectedPairingEvents: ICrewEvent[] = useMemo(() => {
    return selectedCrewEvents.filter((evnt: ICrewEvent) => isIDisplayPairing(evnt.event));
  }, [selectedCrewEvents]);

  const nonFlyingAssignmentEvents: ICrewEvent[] = useMemo(() => {
    return selectedCrewEvents.filter((event: ICrewEvent) => isINonFlyingAssignment(event.event));
  }, [selectedCrewEvents]);

  const contextMenuHeader: string | JSX.Element = useMemo(() => {
    switch (true) {
      case selectedPairingEvents?.length > 0:
        return (
          <Box sx={{ width: "100%", display: "flex" }}>
            Pairing{selectedPairingEvents.length > 1 ? "s" : ""}:
            {selectedPairingEvents.map((evnt, key) => {
              const pairing = evnt.event as IDisplayPairing | IDisplayBidPairing;
              return (
                <ShortCodeChip
                  code={pairing?.shortId}
                  itemId={pairing?._id}
                  type="pairing"
                  isPlanned={evnt.isPlanned}
                  contextMenuId={id}
                  key={key}
                />
              );
            })}
          </Box>
        );
      case nonFlyingAssignmentEvents?.length > 0: {
        const nfa = nonFlyingAssignmentEvents[0] as any;
        return (
          <Tooltip title="Non Flying ASsignment">
            <>NFA: {nonFlyingAssignmentEvents.length > 1 ? "Multiple" : nfa?.event?.assignmentType?.title}</>
          </Tooltip>
        );
      }
      case !!selectedCrewDays:
        return `${headerCrewMember?.firstName ?? ""} ${headerCrewMember?.lastName ?? ""}${
          dateLabel ? ` - ${dateLabel}` : ""
        }`;
      default:
        return `${headerCrewMember?.firstName ?? ""} ${headerCrewMember?.lastName ?? ""}`;
    }
  }, [
    selectedPairingEvents,
    nonFlyingAssignmentEvents,
    selectedCrewDays,
    headerCrewMember?.firstName,
    headerCrewMember?.lastName,
    dateLabel,
  ]);

  useEffect(() => {
    if (selectedCrewEvents?.length < 1 && !selectedCrewDays && !isSelectedCrewMember) {
      dispatch(closeContextMenu(id));
    }
  }, [dispatch, id, selectedCrewEvents, selectedCrewDays, isSelectedCrewMember]);

  const open = useMemo(() => {
    const contextMenuState = allMenus.get(`${id}`);
    return contextMenuState?.isOpen ?? false;
  }, [allMenus, id]);

  const executeViolationCheck = (): void => {
    if (selectedCrewDays) {
      dispatch(runRegsValidation(selectedCrewDays.crewId, selectedCrewDays.selectedTimes[0]));
    }
    if (selectedCrewEvents.length === 1) {
      dispatch(runRegsValidation(selectedCrewEvents[0].crewId, selectedCrewEvents[0].event.end));
    }
  };

  return (
    <ContextMenu
      key={`${id}-contextMenu`}
      id={`${id}`}
      menuHeader={contextMenuHeader}
      isOpen={open}
      containerRef={containerRef}
    >
      <>
        {selectedPairingEvents?.length > 0 && (
          <PairingContext
            id={id}
            path={props.path}
            selectedPairingEvents={selectedPairingEvents}
            setActiveEventModal={setActiveEventModal}
          />
        )}

        {nonFlyingAssignmentEvents?.length > 0 && (
          <NonFlyingAssignmentContext id={id} path={props.path} nonFlyingAssignmentEvents={nonFlyingAssignmentEvents} />
        )}

        {selectedCrewEvents?.length === 0 && (
          <SelectedCrewDayContext
            id={id}
            path={props.path}
            selectedCrewDays={selectedCrewDays}
            setActiveEventModal={setActiveEventModal}
          />
        )}

        {import.meta.env.MODE === "development" && (
          <Accordion defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`${props.id}-dev-panelContent`}
              id={`${props.id}-dev-panelHeader`}
            >
              <Box>Developer Actions</Box>
            </AccordionSummary>
            <AccordionDetails>
              <Button
                variant="contained"
                color="dev"
                sx={{ fontSize: "0.8em" }}
                size="small"
                fullWidth
                onClick={executeViolationCheck}
                startIcon={<EngineeringIcon />}
              >
                Run Regulations Validation
              </Button>
            </AccordionDetails>
          </Accordion>
        )}
      </>
    </ContextMenu>
  );
};

export default CrewScheduleContextMenu;
